import { useEffect, useRef, useSyncExternalStore } from "react";
import { ActiveChartTracker, useLinkGroups, type LinkTarget } from "@helm/core";
import type { ChartIndicatorsState } from "@helm/core/indicators";
import type { OverlayOptions } from "@helm/chart";

/**
 * The window's "active chart" (Q-G3, owner 2026-09-28): the chart module used last — pointer down
 * or focus anywhere in it, in the main window or a pop-out — and inside a split module its active
 * pane. The window-wide top toolbar and the right sidebar's watchlist drive it. Rules (MRU
 * fallback when it closes, no stealing on mount) are `ActiveChartTracker` in @helm/core.
 *
 * Each chart module (ChartPanel) registers a controller with `useActiveChartRegistration`: a
 * snapshot of its active pane plus the same setters its own header uses, so the toolbar changes
 * exactly what the pane header would (symbol → pane + link group, timeframe, indicators, layers).
 */

export type ActiveChartSnapshot = {
  /** The active pane's symbol (null = none yet). */
  symbol: string | null;
  tf: string;
  rangePhantoms: boolean;
  /** The active pane's stored indicators (sanitised by the Indicators button). */
  indicators: unknown;
  overlays: OverlayOptions;
  /** The module's link colour; the active pane is its member. */
  link: LinkTarget;
  /** Panes in the module (1 = a plain chart) and which one is active (0-based). */
  paneCount: number;
  pane: number;
  layoutName: string;
};

export type ChartController = {
  snapshot: ActiveChartSnapshot;
  setSymbol: (symbol: string) => void;
  setTimeframe: (tf: string) => void;
  setRangePhantoms: (on: boolean) => void;
  setIndicators: (s: ChartIndicatorsState) => void;
  setOverlays: (o: OverlayOptions) => void;
  openLayoutPicker: () => void;
  /** Chart templates menu / Compare picker of the active pane (owner 2026-09-28). */
  openTemplates?: () => void;
  openCompare?: () => void;
};

export const activeCharts = new ActiveChartTracker<ChartController>();

let seq = 0;

/**
 * ChartPanel: register this module's controller for as long as it is mounted, keep its snapshot
 * current. Returns `markActive` for its pointer-down / focus handlers and whether it is the active
 * chart (the module outline while the toolbar is shown).
 */
export function useActiveChartRegistration(controller: ChartController): {
  markActive: () => void;
  active: boolean;
} {
  const idRef = useRef<string>("");
  if (idRef.current === "") idRef.current = `chart-${String(++seq)}`;
  const id = idRef.current;
  // The tracker holds one stable object per module; its fields follow every render.
  const stable = useRef<ChartController | null>(null);
  stable.current ??= { ...controller };
  const prev = stable.current.snapshot;
  Object.assign(stable.current, controller);

  useEffect(() => {
    const s = stable.current;
    if (s) activeCharts.register(id, s);
    return () => {
      activeCharts.unregister(id);
    };
  }, [id]);

  const snap = controller.snapshot;
  const changed =
    prev.symbol !== snap.symbol ||
    prev.tf !== snap.tf ||
    prev.rangePhantoms !== snap.rangePhantoms ||
    prev.indicators !== snap.indicators ||
    prev.overlays !== snap.overlays ||
    prev.link !== snap.link ||
    prev.paneCount !== snap.paneCount ||
    prev.pane !== snap.pane ||
    prev.layoutName !== snap.layoutName;
  useEffect(() => {
    if (changed) activeCharts.changed(id);
  });

  const active = useSyncExternalStore(activeCharts.subscribe, () => activeCharts.activeId === id);
  return {
    markActive: () => {
      activeCharts.activate(id);
    },
    active,
  };
}

const getRev = (): number => activeCharts.revision;

/** The active chart's controller (re-renders when it, or its active pane's state, changes). */
export function useActiveChart(): ChartController | null {
  useSyncExternalStore(activeCharts.subscribe, getRev);
  return activeCharts.active;
}

/**
 * Load a symbol "into the active chart (and its link group)": the active chart's active pane via
 * its own symbol path (which publishes to the module's link colour). With no chart open, the red
 * link group is retargeted instead, so the DOM / order pad on red still follow. Returns what was
 * targeted.
 */
export function loadSymbol(symbol: string): "chart" | "link" {
  const c = activeCharts.active;
  if (c) {
    c.setSymbol(symbol);
    return "chart";
  }
  useLinkGroups.getState().setSymbol("red", symbol);
  return "link";
}
